import { useTranslation } from '@cobliteam/i18n';
import { Button, Dialog, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { useMemo, useState } from 'react';
import { QuestionMultipleChoice, QuestionOpen, QuestionRating, QuestionSingleChoice } from './questions';
import type { Survey, SurveyQuestion, SurveyResponses } from './types';

interface Props {
  survey: Survey;
  onSubmit: (responses: SurveyResponses) => void;
  onDismiss: () => void;
}

const isAnswered = (question: SurveyQuestion, value: SurveyResponses[number] | undefined): boolean => {
  if (question.type === 'multiple_choice') return Array.isArray(value) && value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== undefined && value !== null;
};

const SurveyDialog = ({ survey, onSubmit, onDismiss }: Props) => {
  const { t } = useTranslation('survey');
  const [responses, setResponses] = useState<SurveyResponses>({});
  const [done, setDone] = useState(false);

  const questions = useMemo(() => survey.questions ?? [], [survey.questions]);

  const visibleQuestions = useMemo(() => {
    const out: { question: SurveyQuestion; index: number }[] = [];
    for (let i = 0; i < questions.length; i++) {
      out.push({ question: questions[i], index: i });
      const prev = questions[i];
      if (!(prev.optional || isAnswered(prev, responses[i]))) break;
    }
    return out;
  }, [questions, responses]);

  const canSubmit = questions.every((q, i) => q.optional || isAnswered(q, responses[i]));

  const setValue = (index: number, next: SurveyResponses[number]) => {
    setResponses((prev) => ({ ...prev, [index]: next }));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(responses);
    setDone(true);
  };

  if (done) {
    return (
      <Dialog open onOpenChange={(open) => !open && onDismiss()}>
        <Dialog.Content size='sm'>
          <Dialog.Header>
            <div className='flex h-12 w-12 items-center justify-center rounded-full bg-primary-2'>
              <Icon name='check' className='text-primary-9' />
            </div>
            <Dialog.Title>{t('thanks_title')}</Dialog.Title>
            <Dialog.Description className='sr-only'>{t('thanks_body')}</Dialog.Description>
          </Dialog.Header>
          <Dialog.Body>
            <Typography variant='text-md'>{t('thanks_body')}</Typography>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant='outlined' onClick={onDismiss}>
              {t('close')}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <Dialog.Content size='md'>
        <Dialog.Header>
          <Dialog.Title className='text-base'>{visibleQuestions[0]?.question.question ?? t('title')}</Dialog.Title>
          <Dialog.Description className='sr-only'>{survey.description ?? t('title')}</Dialog.Description>
        </Dialog.Header>
        <Dialog.Body className='flex flex-col gap-5'>
          {visibleQuestions.map(({ question, index }, position) => {
            const value = responses[index];
            const showHeading = position > 0;
            return (
              <div key={question.id ?? index} className='flex flex-col gap-2'>
                {question.type === 'rating' && (
                  <QuestionRating question={question} value={value as number | undefined} onChange={(v) => setValue(index, v)} hideTitle={!showHeading} />
                )}
                {question.type === 'single_choice' && (
                  <QuestionSingleChoice question={question} value={value as string | undefined} onChange={(v) => setValue(index, v)} hideTitle={!showHeading} />
                )}
                {question.type === 'multiple_choice' && (
                  <QuestionMultipleChoice question={question} value={value as string[] | undefined} onChange={(v) => setValue(index, v)} hideTitle={!showHeading} />
                )}
                {question.type === 'open' && <QuestionOpen question={question} value={value as string | undefined} onChange={(v) => setValue(index, v)} hideTitle={!showHeading} />}
              </div>
            );
          })}
        </Dialog.Body>
        <Dialog.Footer className='flex items-center justify-between gap-2'>
          <Button variant='outlined' onClick={onDismiss} aria-label={t('dismiss_aria')}>
            {t('skip')}
          </Button>
          <Button variant='outlined' onClick={handleSubmit} disabled={!canSubmit}>
            {t('submit')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

export default SurveyDialog;
